export const home = {
  nav: {
    ariaPrimary: "Primary navigation",
    cta: "Talk to the desk",
    ghost: "Sign in",
    links: {
      product: "Product",
      api: "API",
      security: "Security",
      pricing: "Pricing",
      faq: "FAQ",
      contact: "Contact",
    },
  },
  hero: {
    tag: "Multi-network · 24/7 settlement",
    titleLine1: "Charge in any currency.",
    titleLine2Prefix: "Settle",
    titleLine2Highlight: "in yours.",
    description:
      "Fiat, stablecoin and crypto in a single flow. Proprietary liquidity for instant delivery. Institutional operations built in.",
    cta: "Talk to the desk",
    ctaSecondary: "See the API",
    imageAlt: "Multi-asset operations and cross-currency settlement",
    trustLabel: "Built to operate with",
    flow: {
      label: "Example flow",
      status: "Settled in seconds",
    },
  },
  trustMarquee: {
    eyebrow: "Coverage",
    title: "Supported currencies and rails",
    description:
      "A curated set of fiat, stablecoin and crypto assets — across every major network.",
    assetLabels: {
      USD: "US Dollar",
      BRL: "Brazilian Real",
      EUR: "Euro",
      MXN: "Mexican Peso",
      USDT: "Tether",
      USDC: "USD Coin",
      DAI: "Dai",
      BTC: "Bitcoin",
      ETH: "Ethereum",
    },
  },
  howItWorks: {
    eyebrow: "Flow",
    title: "Three steps. One rail.",
    description:
      "Follow a real payment: charge in USD, customer pays in crypto, you receive in BRL — all within the same rail.",
    steps: {
      "01": {
        kicker: "Issuance",
        title: "You charge in any currency",
        subtitle: "In less than a minute you create the invoice and send the link.",
        description:
          "Define which currency you want to charge and which one you want to receive. The desk shows your customer every available payment option.",
        meta: "Dashboard · API · SDK",
        effect: "Invoice created and ready to receive.",
        artifactLabel: "POST /v1/invoices",
        artifactKeys: {
          charge: "charge",
          receive: "receive",
          payment_options: "payment_options",
        },
      },
      "02": {
        kicker: "Payment",
        title: "Your customer pays as they prefer",
        subtitle: "Fiat, stablecoin or crypto — on any supported network.",
        description:
          "Network validation, FX and compliance are on us. Your customer only picks the most convenient method.",
        meta: "Multi-asset · Multi-network",
        effect: "Payment confirmed. Network and FX validated.",
        fromNote: "Customer sent",
        toNote: "Detected and validated",
        toRail: "equivalent",
      },
      "03": {
        kicker: "Settlement",
        title: "You receive in your currency, instantly",
        subtitle: "Proprietary liquidity closes the loop in seconds.",
        description:
          "No banking window, no third-party confirmation. A signed webhook triggers your automated reconciliation.",
        meta: "Instant settlement",
        effect: "Funds credited. Automated reconciliation triggered.",
      },
    },
  },
  differentials: {
    kicker: "Why the desk",
    title: "What makes the desk the desk.",
    description:
      "Four infrastructure layers built for operators who charge and settle at scale — not for experimenters.",
    hero: {
      title: "Proprietary liquidity",
      subtitle: "Funds arrive now, not tomorrow.",
      description:
        "Instant delivery without banking windows, third-party confirmations or external liquidity. Our own funds close the loop in seconds.",
      metric: {
        value: "~18s",
        label: "average settlement time",
      },
    },
    items: {
      multiativo: {
        title: "Native multi-asset",
        description:
          "Fiat, stablecoin and crypto in a single flow. Not crypto-only nor fiat-only — one rail.",
        proof: "3 classes · 1 rail",
      },
      multiRede: {
        title: "Multi-network with validation",
        description:
          "Automatic cross-network validation. Never sends to a wrong address or network.",
        proof: "Zero network errors",
      },
      institucional: {
        title: "Institutional operations",
        description:
          "Compliance, auditable ledger and environment segregation built in. Your controls, our work.",
        proof: "KYC · AML · BACEN",
      },
    },
  },
  useCases: {
    kicker: "Who the desk is for",
    title: "Does this look like your operation?",
    description:
      "Four profiles, one rail. The desk adapts to your pain — not the other way around.",
    cta: "Talk to the desk",
    cases: {
      exportadora: {
        label: "Exporter",
        painCta: "Charges in USD, settles in BRL",
        headline: "Sell abroad, revenue in Brazil.",
        description:
          "Issue invoices in USD or EUR and settle in BRL with FX and settlement in the same flow — no intermediaries.",
        bullets: [
          "Automatic conversion to the contracted currency",
          "Tax receipt compatible with exports",
          "Settlement in hours, not days",
        ],
      },
      fintech: {
        label: "Fintech",
        painCta: "Accepts crypto, settles in fiat",
        headline: "Crypto billing without becoming an exchange.",
        description:
          "Accept USDT, USDC or BTC from your customer and settle automatically in BRL or USD — no custody, no extra KYC.",
        bullets: [
          "HMAC webhooks for real-time reconciliation",
          "Cross-network validation prevents fund loss",
          "Inherited compliance, no extra burden",
        ],
      },
      plataforma: {
        label: "Platform",
        painCta: "Multi-asset checkout in LATAM",
        headline: "Any currency at checkout.",
        description:
          "International platforms selling in Latin America use the desk to accept local fiat, stablecoin and crypto on one rail.",
        bullets: [
          "API or dashboard integration in less than a day",
          "Transparent volume-tiered fees",
          "Node, Python and Go SDKs with real examples",
        ],
      },
      global: {
        label: "Global Enterprise",
        painCta: "Treasury in 3+ currencies",
        headline: "Multi-currency billing and treasury.",
        description:
          "Companies in multiple countries consolidate billing, FX and treasury on one auditable rail.",
        bullets: [
          "Automated reconciliation via unique reference",
          "Reports by currency, entity and contract",
          "Big 4 compatible auditable ledger",
        ],
      },
    },
  },
  assetsRails: {
    kicker: "Full coverage",
    title: "One rail. Many currencies.",
    description:
      "We start with a curated set — fiat, stablecoin and crypto — covering most payment corridors. More coming soon, always with real validation.",
    linkLabel: "See full catalog",
    moreInBreve: "+ coming soon",
    stats: {
      moedas: "active currencies",
      redes: "networks and rails",
      classes: "asset classes",
    },
    columns: {
      fiat: { label: "Fiat", caption: "Direct bank settlement" },
      stablecoins: { label: "Stablecoins", caption: "Multi-network with validation" },
      crypto: { label: "Crypto", caption: "Native rails" },
    },
    currencyLabels: {
      USD: "US Dollar",
      BRL: "Brazilian Real",
      EUR: "Euro",
      MXN: "Mexican Peso",
      USDT: "Tether",
      USDC: "USD Coin",
      DAI: "Dai",
      BTC: "Bitcoin",
      ETH: "Ethereum",
    },
  },
  security: {
    kicker: "Security & Compliance",
    title: "Institutional controls, from day one.",
    description:
      "Every transaction passes through a real compliance layer. Not marketing — code, logs and policy.",
    note:
      "No invented certifications. Only the controls implemented today, auditable by whoever contracts.",
    pillars: {
      kyc: {
        category: "Onboarding",
        title: "Full KYC / KYB",
        description:
          "Document validation of people and companies before the first trade. Sanctions and PEP screening.",
      },
      aml: {
        category: "Transactional",
        title: "AML monitoring",
        description:
          "Rules and typologies applied in real time. Flags reviewed by human analysts.",
      },
      custody: {
        category: "Custody",
        title: "Segregated custody",
        description:
          "Customer funds kept separate from operational balance. No single vault, no cross risk.",
      },
      ledger: {
        category: "Audit",
        title: "Auditable ledger",
        description:
          "Every movement recorded with chained hashes. Exportable as CSV or API for your accounting team.",
      },
      regulatory: {
        category: "Regulatory",
        title: "Regulatory compliance",
        description:
          "Aligned with BACEN and COAF in Brazil. Documented policies reviewed quarterly.",
      },
      environments: {
        category: "Infrastructure",
        title: "Environment segregation",
        description:
          "Production, staging and sandbox isolated by network and credentials. Ephemeral keys on every deploy.",
      },
    },
  },
  api: {
    kicker: "/ 04 · For developers",
    rail: "API",
    statusLabel: "API operational",
    title: "Everything the desk does, you do via API.",
    description:
      "Versioned REST, HMAC-SHA256 signed webhooks, sandbox identical to production. First call in minutes, not days.",
    tabs: { request: "Request", response: "Response" },
    copyLabel: "Copy",
    copiedLabel: "Copied",
    signature: "BUILT FOR DEVELOPERS",
    specSheetLabel: "Technical specification",
    stats: {
      uptime: "Uptime SLA",
      latency: "P50 latency",
      regions: "Regions",
      sdks: "Official SDKs",
    },
    spec: {
      protocol: { label: "Protocol", value: "REST · HTTP/1.1, HTTP/2", note: "JSON + UTF-8" },
      auth: { label: "Auth", value: "Bearer token + HMAC-SHA256", note: "In-place rotation" },
      limits: { label: "Rate limit", value: "1,000 req/min · burst 50", note: "Ratelimit header" },
      versioning: { label: "Versioning", value: "Semver · 12-month deprecation", note: "Public changelog" },
      sdks: { label: "SDKs", value: "Node · Python · Go · Ruby", note: "Real examples" },
      sandbox: { label: "Sandbox", value: "Full production mirror", note: "Dummy data" },
    },
    ctaPrimary: "Read the docs",
    ctaSecondary: "Request API access",
  },
  pricing: {
    kicker: "/ 09 · Pricing",
    rail: "PRICING",
    tag: "By quote",
    title: "Pricing by quote. No surprises, no hidden fees.",
    description:
      "We work with volume-tiered models, transparent spread and settlement fee agreed per contract. No setup fee.",
    formulaLabel: "Pricing model",
    legend: {
      spread: "Visible bid-ask difference",
      volume: "Monthly tier agreed per contract",
      ativo: "Fiat · stablecoin · crypto",
    },
    attributes: {
      setupFee: { label: "Setup fee", value: "Zero", note: "No entry charge" },
      spread: { label: "Spread", value: "Transparent", note: "Shown on each quote" },
      taxa: { label: "Fee", value: "Volume-tiered", note: "Negotiated per contract" },
      contrato: { label: "Contract", value: "Customizable", note: "Tailor-made terms" },
    },
    process: {
      label: "Process",
      "01": { label: "Initial call", detail: "15 minutes about your case" },
      "02": { label: "Estimate", detail: "Within 1 business day" },
      "03": { label: "Contract", detail: "Written terms" },
    },
    primaryCta: "Talk to the desk",
    ctaHelper: "No one sells. Only assesses your case.",
    note: "Initial estimate within 1 business day after a brief conversation about your case.",
    signature: "TRANSPARENT BY DESIGN",
  },
  faq: {
    kicker: "Frequently asked questions",
    title: "Questions that come before the first call.",
    description: "If any question remains after this, talk to the desk.",
    items: {
      what: {
        question: "What is MesaOTC?",
        answer:
          "Multi-asset billing and settlement infrastructure. You charge in one currency and receive in another — fiat, stablecoin or crypto — with proprietary liquidity and institutional operations built in. Not an exchange, broker or wallet.",
      },
      liquidation: {
        question: "How does settlement work?",
        answer:
          "Your customer pays in the chosen currency and the desk delivers in the receiving currency you defined. We use proprietary liquidity to close the loop — no external brokers or market windows. Typically within minutes after on-chain or bank confirmation.",
      },
      assets: {
        question: "Which currencies and networks do you support?",
        answer:
          "Fiat: BRL (PIX, TED), USD (WIRE, ACH), EUR (SEPA), MXN (SPEI). Stablecoins: USDT (ERC20, TRC20, Polygon), USDC (ERC20, Solana, Base), DAI (ERC20). Crypto: BTC (Mainnet, Lightning), ETH (Mainnet). Additional assets released as demand and validation require.",
      },
      compliance: {
        question: "How is compliance handled?",
        answer:
          "Full KYC and KYB before the first trade (documentation, sanctions, PEP). Real-time AML monitoring with rules and typologies. Segregated custody, auditable ledger with chained hashes, environment segregation (production/staging/sandbox). Aligned with BACEN and COAF.",
      },
      integration: {
        question: "How long does it take to integrate?",
        answer:
          "Most teams make their first sandbox call in less than a day. Production depends on KYB and integration type — dashboard only requires registration; API requires production credentials after validation.",
      },
      costs: {
        question: "What are the costs?",
        answer:
          "Pricing agreed per contract, based on volume tier, currencies operated and networks used. No setup fee. Initial estimate within 1 business day after a brief conversation about your case.",
      },
      sla: {
        question: "Do you have an SLA?",
        answer:
          "Yes. API availability SLA, average settlement time and a dedicated support channel are defined per contract, according to volume tier. Public status at status.mesaotc.com.",
      },
      "api-access": {
        question: "How do I request API access?",
        answer:
          "Request access at contact@mesaotc.com or via the 'Request API access' CTA. After basic validation, we send sandbox credentials immediately; production credentials issue after KYB completes.",
      },
    },
  },
  contact: {
    kicker: "/ 11 · Contact",
    rail: "CONTACT",
    title: "Tell us the case. We'll reply within 1 business day.",
    description:
      "A 15-minute conversation is enough to tell if it's a fit for your operation. No sales pitch.",
    reassuranceSpec: {
      response: { label: "Response", value: "Within 1 business day" },
      assessment: { label: "Assessment", value: "Free, no commitment" },
      privacy: { label: "Privacy", value: "Implicit NDA from the first line" },
    },
    reassurance: [
      "Reply within 1 business day",
      "Free assessment",
      "Information kept confidential",
    ],
    successTitle: "Received.",
    successMessage:
      "We received your message. A consultant will reply within 1 business day.",
    successHint: "In the meantime, you can",
    successLinkLabel: "read the documentation",
    submitCta: "Send message",
    submitMicrocopy: "You can reply to this conversation directly from your inbox.",
    signature: "STARTS A CONVERSATION",
    altChannels: {
      label: "Direct channels",
      directLabel: "Direct",
      agendaLabel: "Schedule",
      agendaValue: "cal.com/mesaotc",
    },
    form: {
      sectionLabel: "Form",
      nameLabel: "Name",
      namePlaceholder: "Your full name",
      nameHint: "How you'd like to be addressed",
      companyLabel: "Company",
      companyPlaceholder: "Company name",
      companyHint: "Legal or trade name",
      emailLabel: "Work email",
      emailPlaceholder: "you@company.com",
      emailHint: "Use a work email",
      volumeLabel: "Estimated volume / month",
      volumePlaceholder: "Select a tier",
      volumeHint: "Approximate monthly range",
      volumeOptions: {
        "ate-30k": "Up to USD 30k",
        "30k-100k": "USD 30k – 100k",
        "100k-500k": "USD 100k – 500k",
        "500k-1m": "USD 500k – 1M",
        "1m-5m": "USD 1M – 5M",
        "5m+": "Above USD 5M",
        avaliando: "Still evaluating",
      },
      messageLabel: "Message",
      messagePlaceholder:
        "Briefly describe your operation — currencies involved, frequency, goal.",
      messageHint: "Up to 1000 characters",
      validation: {
        nameRequired: "Enter your name.",
        companyRequired: "Enter your company name.",
        emailRequired: "Enter your work email.",
        emailInvalid: "Invalid email.",
        emailPersonal: "Use a work email.",
        volumeRequired: "Select a volume tier.",
        messageRequired: "Briefly describe your operation.",
      },
    },
  },
  finalCta: {
    title: "Ready to operate in any currency?",
    description:
      "Connect the desk to your billing and settlement flow in one conversation.",
    primaryCta: "Talk to the desk",
    secondaryCta: "Read the docs",
  },
  footer: {
    tagline: "Multi-asset billing and settlement infrastructure.",
    columns: {
      product: {
        label: "Product",
        links: {
          howItWorks: "How it works",
          useCases: "Use cases",
          assets: "Assets & rails",
          security: "Security",
          pricing: "Pricing",
        },
      },
      developer: {
        label: "Developer",
        links: {
          docs: "Documentation",
          apiReference: "API reference",
          status: "Status",
          changelog: "Changelog",
        },
      },
      company: {
        label: "Company",
        links: {
          useCases: "Use cases",
          contact: "Contact",
          faq: "FAQ",
        },
      },
      legal: {
        label: "Legal",
        links: {
          privacy: "Privacy",
          terms: "Terms",
          compliance: "Compliance",
        },
      },
    },
    meta: {
      address: "São Paulo, Brazil",
      legalName: "MesaOTC Tecnologia Ltda.",
      statusLabel: "Operations stable",
      region: "BRAZIL · LATAM",
      rights: "All rights reserved.",
    },
    languageLabel: "Language",
  },
  theme: {
    toLight: "Switch to light theme",
    toDark: "Switch to dark theme",
  },
} as const
